import { get } from 'lodash';
import { BookingStateProperties } from '../constants';
import patientSelector from '../selectors/PatientSelector';
import bookingStateSelector from '../selectors/BookingStateSelector';
import { getCurrentInsuranceCard } from '../utils/getCurrentInsuranceCard';
import { updateBookingState } from './SchedulingActions';
import { AnalyticsTypes } from '../../middleware/google-analytics';
import { APPOINTMENT_INFO_GA_CATEGORY } from '../pages/AppointmentInfo/constants';
import { GA_ACTIONS, INSURANCE_ID_TYPE } from './constants';
import { INSURANCE_NOT_TAKEN_WARNING } from '../components/AppointmentRequirements/constants';

export const InsuranceCardActions = {
    SELECTED: '@@ica/CARD_SELECTED',
    SHARE_CLICKED: '@@ica/SHARE_CLICKED',
    TRACK_CLICK_CARRIER_DROPDOWN: '@@ica/TRACK_CLICK_CARRIER_DROPDOWN',
    TRACK_CLICK_PLAN_DROPDOWN: '@@ica/TRACK_CLICK_PLAN_DROPDOWN',
    TRACK_PRELOAD_CARRIER: '@@ica/TRACK_PRELOAD_CARRIER',
    TRACK_PRELOAD_PLAN: '@@ica/TRACK_PRELOAD_PLAN',
};

const getSelectCardGaAction = (cards, card) => {
    if (card.isDefault) {
        return cards && cards.length > 0
            ? 'Insurance Card - No Images For Plan'
            : 'Insurance Card - No Images At All';
    } else {
        return card.cardHasPlan
            ? 'Insurance Card - Displayed For Plan'
            : 'Insurance Card - Displayed With No Plan';
    }
};

const setCard = ({
    cards,
    card,
    analytics = [],
    isCarrierChange,
    isPlanChange,
}) => {
    return {
        type: InsuranceCardActions.SELECTED,
        payload: card,
        isCarrierChange,
        isPlanChange,
        meta: {
            analytics: [
                ...analytics,
                {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: getSelectCardGaAction(cards, card),
                },
            ],
        },
    };
};

export function setPatientAndInsuranceCard(currentPatientId) {
    return (dispatch, getState) => {
        const state = getState();
        const bookingState = bookingStateSelector(state);
        const { insuranceCards } = patientSelector(state);

        const currentCard = getCurrentInsuranceCard({
            state,
            insuranceCards,
            currentPatientId,
        });

        dispatch(
            updateBookingState({
                ...bookingState,
                [BookingStateProperties.patientId]: currentPatientId,
                [BookingStateProperties.insuranceCardDataId]:
                    currentCard.availableInsuranceCardDataId,
                [BookingStateProperties.isInsuranceCardShared]: true,
            })
        );

        dispatch(
            setCard({
                cards: insuranceCards,
                card: currentCard,
            })
        );
    };
}

export function setCarrierState(carrierId) {
    return (dispatch, getState) => {
        const state = getState();
        const bookingState = bookingStateSelector(state);
        const { insuranceCards } = patientSelector(state);

        const currentCard = getCurrentInsuranceCard({
            state,
            insuranceCards,
        });

        const requirements = get(bookingState, 'requirements', []);
        const newRequirements = requirements.filter(
            req => req.key !== INSURANCE_NOT_TAKEN_WARNING
        );

        dispatch(
            updateBookingState({
                ...bookingState,
                [BookingStateProperties.insuranceCarrierId]: carrierId,
                [BookingStateProperties.insurancePlanId]: undefined,
                [BookingStateProperties.insuranceCardDataId]: null,
                [BookingStateProperties.isInsuranceCardShared]: true,
                requirements: newRequirements,
            })
        );

        dispatch(
            setCard({
                cards: insuranceCards,
                card: currentCard,
                analytics: [
                    {
                        type: AnalyticsTypes.TrackEvent,
                        category: APPOINTMENT_INFO_GA_CATEGORY,
                        action: 'Insurance Carrier Selection',
                        eventDetails: {
                            InsuranceCarrierId: carrierId,
                            SelectionType: INSURANCE_ID_TYPE.CARRIER,
                        },
                    },
                ],
                isCarrierChange: true,
            })
        );
    };
}

export function setPlanState(planId = '-1') {
    return (dispatch, getState) => {
        const state = getState();
        const bookingState = bookingStateSelector(state);
        const { insuranceCards } = patientSelector(state);

        const currentCard = getCurrentInsuranceCard({
            state,
            insuranceCards,
            planId,
        });

        const requirements = get(bookingState, 'requirements', []);
        const newRequirements = requirements.filter(
            req => req.key !== INSURANCE_NOT_TAKEN_WARNING
        );

        dispatch(
            updateBookingState({
                ...bookingState,
                [BookingStateProperties.insurancePlanId]: planId,
                [BookingStateProperties.insuranceCardDataId]:
                    currentCard.availableInsuranceCardDataId,
                [BookingStateProperties.isInsuranceCardShared]: true,
                requirements: newRequirements,
            })
        );

        dispatch(
            setCard({
                cards: insuranceCards,
                card: currentCard,
                analytics: [
                    {
                        type: AnalyticsTypes.TrackEvent,
                        category: APPOINTMENT_INFO_GA_CATEGORY,
                        action: 'Insurance Plan Selection',
                        eventDetails: {
                            InsurancePlanId: planId,
                            SelectionType: INSURANCE_ID_TYPE.PLAN,
                        },
                    },
                ],
                isPlanChange: true,
            })
        );
    };
}

export function trackClickCarrierDropdown() {
    return {
        type: InsuranceCardActions.TRACK_CLICK_CARRIER_DROPDOWN,
        meta: {
            analytics: [
                {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: GA_ACTIONS.ClickInsuranceCarrierDropdown,
                },
            ],
        },
    };
}

export function trackClickPlanDropdown() {
    return {
        type: InsuranceCardActions.TRACK_CLICK_PLAN_DROPDOWN,
        meta: {
            analytics: [
                {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: GA_ACTIONS.ClickInsurancePlanDropdown,
                },
            ],
        },
    };
}

export function trackPreloadInsuranceCarrier(insuranceCarrierId) {
    return {
        type: InsuranceCardActions.TRACK_PRELOAD_CARRIER,
        meta: {
            analytics: [
                {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: GA_ACTIONS.PreloadInsuranceCarrier,
                    eventDetails: {
                        InsuranceCarrierId: insuranceCarrierId,
                        SelectionType: INSURANCE_ID_TYPE.CARRIER,
                    },
                },
            ],
        },
    };
}

export function trackPreloadInsurancePlan(insurancePlanId) {
    return {
        type: InsuranceCardActions.TRACK_PRELOAD_PLAN,
        meta: {
            analytics: [
                {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: GA_ACTIONS.PreloadInsurancePlan,
                    eventDetails: {
                        InsurancePlanId: insurancePlanId,
                        SelectionType: INSURANCE_ID_TYPE.PLAN,
                    },
                },
            ],
        },
    };
}

export function initializeCards() {
    return (dispatch, getState) => {
        const state = getState();
        const bookingState = bookingStateSelector(state) || {};
        const stateCardId = (
            bookingState[BookingStateProperties.insuranceCardDataId] || -1
        ).toString();
        const isCardShared =
            bookingState[BookingStateProperties.isInsuranceCardShared] || false;
        const { insuranceCards } = patientSelector(state);

        const currentCard = getCurrentInsuranceCard({
            state,
            insuranceCards,
        });

        if (!currentCard.isDefault && currentCard.id !== stateCardId) {
            dispatch(
                updateBookingState({
                    ...bookingState,
                    [BookingStateProperties.insuranceCardDataId]:
                        currentCard.availableInsuranceCardDataId,
                    [BookingStateProperties.isInsuranceCardShared]: true,
                })
            );
        } else if (!currentCard.isDefault && !isCardShared) {
            dispatch(
                updateBookingState({
                    ...bookingState,
                    [BookingStateProperties.isInsuranceCardShared]: true,
                })
            );
        }

        dispatch(
            setCard({
                cards: insuranceCards,
                card: currentCard,
            })
        );
    };
}

export function handleShareInsuranceCheckboxChange(isInsuranceCardShared) {
    return (dispatch, getState) => {
        const state = getState();
        const bookingState = bookingStateSelector(state);
        const { selectedInsuranceCard } = patientSelector(state);
        const { memberIdFromCard } = selectedInsuranceCard;

        dispatch({
            type: InsuranceCardActions.SHARE_CLICKED,
            payload: {
                isInsuranceCardShared,
                memberIdFromCard,
            },
            meta: {
                analytics: {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: 'Toggled Insurance Card Checkbox',
                },
            },
        });

        dispatch(
            updateBookingState({
                ...bookingState,
                [BookingStateProperties.isInsuranceCardShared]: isInsuranceCardShared,
            })
        );
    };
}

export function handleClearInsuranceFromModal() {
    return (dispatch, getState) => {
        const state = getState();
        const bookingState = bookingStateSelector(state);
        const { insuranceCards } = patientSelector(state);

        const currentCard = getCurrentInsuranceCard({
            state,
            insuranceCards,
        });

        dispatch(
            updateBookingState({
                ...bookingState,
                [BookingStateProperties.insuranceCarrierId]: '-2',
                [BookingStateProperties.insurancePlanId]: undefined,
                [BookingStateProperties.insuranceCardDataId]: null,
                [BookingStateProperties.isInsuranceCardShared]: true,
            })
        );

        dispatch(
            setCard({
                cards: insuranceCards,
                card: currentCard,
                analytics: [
                    {
                        type: AnalyticsTypes.TrackEvent,
                        category: 'Booking - Modal',
                        action:
                            'Clicked Update Insurance After Out of Network Warning',
                    },
                ],
                isCarrierChange: true,
            })
        );
    };
}
